// highlight.ts — pure helpers for rendering Phase 1a's highlight payloads
// (CTSK-493 §5.3; design doc §6.4). No React, unit-tested in isolation.
//
// Title: the backend ships `titleHighlights: [[start, end), …]` as JS
// string indices — we slice-and-wrap, no client-side regex.
//
// Snippet: the backend ships escaped plain text with ONLY our `<mark>`/
// `</mark>` added (strip-all → escape → mark; CTSK-492 decision 4). The
// client belt-and-suspenders mirrors that posture rather than trusting
// it: split on the mark tokens, strip ANY other tag from each text
// segment, decode the five entities escapeHtml emits, and hand back
// plain-text segments for React to render as text nodes. Nothing from
// the payload is ever interpreted as markup — `dangerouslySetInnerHTML`
// is not used at all (AC #10).

import type { DocumentSearchResult, TitleHighlight } from '@captain-tasko/shared';

export interface TextSegment {
  text: string;
  marked: boolean;
}

/** Clamp, sort, merge and drop degenerate ranges so slicing is safe. */
export function normalizeRanges(ranges: readonly TitleHighlight[] | undefined, length: number): TitleHighlight[] {
  if (!ranges || ranges.length === 0 || length === 0) return [];
  const clamped: TitleHighlight[] = [];
  for (const r of ranges) {
    if (!Array.isArray(r) || r.length < 2) continue;
    const s = Math.max(0, Math.min(length, Math.floor(Number(r[0]))));
    const e = Math.max(0, Math.min(length, Math.floor(Number(r[1]))));
    if (Number.isNaN(s) || Number.isNaN(e) || e <= s) continue;
    clamped.push([s, e]);
  }
  clamped.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const out: TitleHighlight[] = [];
  for (const r of clamped) {
    const last = out[out.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
    else out.push([r[0], r[1]]);
  }
  return out;
}

/** Title → segments via backend offsets. */
export function titleSegments(title: string, highlights: readonly TitleHighlight[] | undefined): TextSegment[] {
  const ranges = normalizeRanges(highlights, title.length);
  if (ranges.length === 0) return title ? [{ text: title, marked: false }] : [];
  const out: TextSegment[] = [];
  let cursor = 0;
  for (const [s, e] of ranges) {
    if (s > cursor) out.push({ text: title.slice(cursor, s), marked: false });
    out.push({ text: title.slice(s, e), marked: true });
    cursor = e;
  }
  if (cursor < title.length) out.push({ text: title.slice(cursor), marked: false });
  return out;
}

const MARK_TOKEN = /(<\/?mark>)/i;

/** Remove every `<...>` tag and HTML comment. Mirrors api stripTags. */
export function stripTags(s: string): string {
  return s.replace(/<!--[\s\S]*?-->/g, '').replace(/<[^>]*>/g, '');
}

/** Decode exactly the entities the backend's escapeHtml produces. */
export function decodeEntities(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
}

/**
 * Snippet (backend-rendered `<mark>` HTML) → plain-text segments.
 * Unbalanced marks are tolerated: a stray `</mark>` is ignored, an
 * unclosed `<mark>` marks through to the end. Empty input → [].
 */
export function snippetSegments(snippet: string | null | undefined): TextSegment[] {
  if (!snippet) return [];
  const out: TextSegment[] = [];
  let marked = false;
  for (const part of snippet.split(MARK_TOKEN)) {
    if (part === '') continue;
    const lower = part.toLowerCase();
    if (lower === '<mark>') { marked = true; continue; }
    if (lower === '</mark>') { marked = false; continue; }
    const text = decodeEntities(stripTags(part));
    if (text === '') continue;
    const last = out[out.length - 1];
    if (last && last.marked === marked) last.text += text;
    else out.push({ text, marked });
  }
  return out;
}

/** Plain text of a snippet (for aria / tests). */
export function snippetText(snippet: string | null | undefined): string {
  return snippetSegments(snippet).map((s) => s.text).join('');
}

/** Stable DOM id for an option — `aria-activedescendant` target. */
export function optionIdFor(result: Pick<DocumentSearchResult, 'id'>): string {
  return `document-search-option-${result.id}`;
}
